import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    DATE_TIME_FORMAT_NAME_MODEL,
    EMPTY_VALUES,
    currencyFormat,
    getDateFormat,
    getTimeFormat
} from '../../../../../../Apis/functions';
import { SEARCH_MODEL } from '../../../../../../components/table/Model/TableModel';
import CustomTh from '../../../../../../components/table/CustomTh';
import CustomTd from '../../../../../../components/table/CustomTd';
import { DetailsIconsOptions, IconClockTable, IconScheduleTable} from '../../../../../../components/buttons/IconButtons';
import DropDownMenuProfile from '../../../../../../components/DropDown/DropDownMenuProfile';
import DropDownMenuItem from '../../../../../../components/DropDown/DropDownMenuItem';
import { SimpleSearch } from '../../../../../../reduxToolkit/Search/SearchApi';
import {
    GetPayrollResultPeriodYears,
    GetFinishedPayrollResultPeriodsByYear
} from '../../../../../../reduxToolkit/PayrollPolicy/PayrollPolicyApi';
import TableFunction from '../../../../../../components/table/TableFunction';
import HandleChange from '../../../../../../Helpers/HandleChange';
import { PAYROLL_HISTORY_FILTER_MODEL } from './Model/Model';
import routes from '../../../../../../Routes/routes';
import CustomChip from '../../../../../../components/CustomChip';
import { colors } from '../../../../../../enums/colors';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Logic hook for the payroll history table. Adds year and period filters on top
 * of the active payroll table behaviour.
 */

const PayrollHistoryTableApp = ({
    searchElement,
    setBodyForExport,
    option,
    modified,
    setModified,
    activeStep,
    setExportReady,
    subView
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const tableId = 'tbl_pht_payroll_history';
    const searchId = 'txt_pht_payroll_history';
    // Filters selected before opening a user detail, so coming back keeps the view.
    const restoredFilters = location?.state?.payrollHistoryFilters;
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const [filters, setFilters] = useState(() => PAYROLL_HISTORY_FILTER_MODEL.set(restoredFilters));
    const { year, period } = filters;
    const [yearList, setYearList] = useState([]);
    const [periods, setPeriods] = useState([]);
    const { handleChange } = HandleChange({ model: filters, setModel: setFilters });

    // Load the finished-period catalog for the given year.
    const fetchPeriodsByYear = (selectedYear) => {
        dispatch(GetFinishedPayrollResultPeriodsByYear(selectedYear)).then(({ payload: { data: { data, success } } }) => {
            if (success === true) setPeriods(data ?? []);
        });
    };

    // Load the year catalog and the periods for the default year on mount.
    useEffect(() => {
        dispatch(GetPayrollResultPeriodYears()).then(({ payload: { data: { data, success } } }) => {
            if (success === true) {
                const list = (data ?? []).map((item) => {
                    const value = item?.year ?? item;
                    return { id: value, name: `${value}`, value };
                });
                setYearList(list);
            }
        });

        if (restoredFilters == null) return;
        // A restored year needs its period catalog so the period select can label it.
        if (restoredFilters?.year != null) fetchPeriodsByYear(restoredFilters.year);

        // The filters were consumed, so they are dropped from the route state. Only that
        // key is removed: the tab and sub-view keys still drive the view on re-renders.
        const { payrollHistoryFilters, ...rest } = location.state;
        navigate(location.pathname, { replace: true, state: rest });
    }, []);

    // Period options for the selected year. Value is the period id; label is its date range.
    const periodList = periods
        .map((p, index) => ({
            id: p?.id,
            value: p?.id,
            name: `${t('period')} ${index+1}: ${getDateFormat(p?.startDate, true, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)} - ${getDateFormat(p?.endDate, true, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}`,
        }));

    const getThead = () => [
        <CustomTh tableId={tableId} classes={"first-head"}>{t('name')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('department')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('calculated_at')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('base_salary')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('total_earnings')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('gross_salary')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('total_deductions')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('net_pay')}</CustomTh>,
    ];

    const getTbody = () => {
        if (!results?.content?.length) return { body: [], data: [] };

        const body = results.content.map((row) => (
            <>
                <CustomTd tableId={tableId} classes={"first-column"}>
                    {row?.fullName}
                </CustomTd>
                <CustomTd tableId={tableId}>
                    {row?.department ? <CustomChip
                        background={colors.secondary100}
                        borderColor={colors.secondary150}
                        fontColor={colors.secondary700}
                        legend={row?.department}
                    /> : EMPTY_VALUES.DEFAULT}
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <div className={"d-flex align-items-center mb-1"}>
                        <IconScheduleTable className={"mr-8px icon-color-general-950"}/>
                        {getDateFormat(row?.calculatedAt, false)}
                    </div>
                    <div className={"d-flex align-items-center"}>
                        <IconClockTable className={"mr-8px icon-color-general-950"}/>
                        {getTimeFormat(row?.calculatedAt)}
                    </div>
                </CustomTd>
                <CustomTd tableId={tableId}>
                    {`$${currencyFormat(row?.baseSalary)}`}
                </CustomTd>
                <CustomTd tableId={tableId}>
                    {`$${currencyFormat(row?.totalOvertime)}`}
                </CustomTd>
                <CustomTd tableId={tableId}>
                    {`$${currencyFormat(row?.grossSalary)}`}
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <span className={"font-color-accent-550 ibl-font-w-600"}>{`-$${currencyFormat(row?.totalDeductions)}`}</span>
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <span className={"ibl-font-w-600"}>{`$${currencyFormat(row?.netPay)}`}</span>
                </CustomTd>
            </>
        ));

        return { body, data: results.content };
    };

    const getActionsButtons = (row, index) => (
        <DropDownMenuProfile
            openMenu={openMenu}
            handleMenuToggle={handleMenuToggle}
            menuId={`menu-pht-${row?.userId}`}
        >
            <DropDownMenuItem
                id={`btn_pht_detail_${row?.userId}`}
                index={index}
                handleClose={handleClose}
                name={t('details')}
                click={() => navigate(routes.payrollUser.details, { state: {
                    userId: row?.userId,
                    payrollResultId: row?.id,
                    currentTab: activeStep,
                    currentSubView: subView,
                    payrollHistoryFilters: { year, period },
                } })}
                icon={<DetailsIconsOptions />}
            />
        </DropDownMenuProfile>
    );

    const getData = (page, { searchElement } = {}) => {
        // Require both a year and a period before fetching.
        if (!filters.isValid()) return;
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model: option,
            key: searchElement,
            viewType: year,
            modelId: period
        });

        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success === true) setResults(data);
        });
    };

    const { thead, tbody, pagination, rowsPerPage, openMenu, handleMenuToggle, handleClose } = TableFunction({
        getTbody,
        getThead,
        data: results,
        labelRowsPerPage: 'employees_per_page',
        hasContainer: true,
        fixedPagination: false,
        searchElement,
        getData,
        modified,
        setModified,
    });

    // Changing the year updates the model, clears the period and reloads its periods.
    const handleYearChange = (evt) => {
        handleChange(evt);
        const value = evt?.target?.value ?? null;
        setFilters((prev) => ({ ...prev, period: null }));
        if (value !== null) fetchPeriodsByYear(value);
    };

    // Re-fetch from page 0 whenever a filter changes. The TableFunction mount fetch
    // runs while `year` is still null (no-op), so the first real fetch happens here
    // once the default year resolves.
    useEffect(() => {
        getData(0, { searchElement });
    }, [year, period]);

    // Show the export button only once a year and a period are selected.
    useEffect(() => {
        if (setExportReady) setExportReady(filters.isValid());
        return () => { if (setExportReady) setExportReady(false); };
    }, [year, period]);

    return {
        setResults,
        rowsPerPage,
        tableId,
        searchId,
        thead,
        tbody,
        pagination,
        getActionsButtons,
        year,
        setYear: handleYearChange,
        period,
        setPeriod: handleChange,
        yearList,
        periodList,
        getData
    };
};

export default PayrollHistoryTableApp;
